import axios from "axios";
import { getAssetPdf, readPdfError } from "../model/AssetUpload";

// API Functions for Asset Details
export class AssetDetailsAPI {

  // Fetch asset details by ID
  static async fetchAssetDetails(assetId) {
    try {
      let scanSessionId = sessionStorage.getItem("scan_session_id");

      if (!scanSessionId) {
        scanSessionId = crypto.randomUUID();
        sessionStorage.setItem("scan_session_id", scanSessionId);
      }
      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL}/asset_it/asset-details?id=${assetId}&session_id=${scanSessionId}`
      );

      if (response.data.message === "Asset not found") {
        throw new Error("Asset not found");
      }

      return response.data.data;
    } catch (error) {
      console.error("Error fetching asset details:", error);
      throw error;
    }
  }

  // Validate token
  static async validateToken(token) {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL}/asset_it/asset-token`,
        {
          headers: {
            "x-api-key": token,
          },
        }
      );

      return response.status === 200;
    } catch (error) {
      console.log("Token validation failed:", error);
      return false;
    }
  }

  // Generate and download PDF
  // sizeId / saveSize: size chosen in the "Choose sticker size" popup (optional)
  static async generateAssetPdf(assetId, handleToastOpen, sizeId, saveSize) {
    try {
      const getFormattedDateTime = () => {
        const currentDate = new Date();
        const formattedDate = currentDate.toISOString().slice(0, 10);
        const currentTime = currentDate.toLocaleTimeString();
        return formattedDate + "_" + currentTime;
      };

      const response = await getAssetPdf(assetId, "print_view_id", sizeId, saveSize);
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `asset_${assetId || "all"}_${getFormattedDateTime()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const { status, data } = await readPdfError(error);
      if (status === 409 && data?.need_size) {
        // No sticker size -> the page shows the "Choose sticker size" popup
        const askError = new Error(data.message);
        askError.needSize = data;
        throw askError;
      }
      handleToastOpen("error", data?.message || error.message);
      throw error;
    }
  }

  // Fetch full assignment history of an asset (newest first)
  static async fetchAssetHistory(assetId) {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${process.env.REACT_APP_BE_URL}/asset_it/asset-history?id=${encodeURIComponent(assetId)}`,
      { headers: { "x-api-key": token } }
    );
    return response.data.data || [];
  }

  // Return an asset (reason and condition are required, notes optional)
  static async returnAsset(assetId, returnReason, returnCondition, returnNotes) {
    const token = localStorage.getItem("token");
    const response = await axios.put(
      `${process.env.REACT_APP_BE_URL}/asset_it/return-asset`,
      {
        asset_id: assetId,
        return_reason: returnReason,
        return_condition: returnCondition,
        return_notes: returnNotes,
      },
      { headers: { "x-api-key": token, "Content-Type": "application/json" } }
    );
    return response.data;
  }

  // Tell other open tabs (e.g. the asset list) that an asset changed
  static notifyAssetChanged(assetId) {
    try {
      const channel = new BroadcastChannel("asset-updates");
      channel.postMessage({ asset_id: assetId, at: Date.now() });
      channel.close();
    } catch (e) {
      // old browser: the storage event below still works
    }
    try {
      localStorage.setItem("asset_updated_at", String(Date.now()));
    } catch (e) {
      // ignore
    }
  }

  // Repair history of one asset (newest first)
  static async fetchRepairHistory(assetId) {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${process.env.REACT_APP_BE_URL}/asset_it/repair-history?id=${encodeURIComponent(assetId)}`,
      { headers: { "x-api-key": token } }
    );
    return response.data.data || [];
  }

  // Send for repair: { issue, vendor, notes }
  static async sendForRepair(assetId, { issue, vendor, notes }) {
    const token = localStorage.getItem("token");
    const response = await axios.put(
      `${process.env.REACT_APP_BE_URL}/asset_it/send-repair`,
      { asset_id: assetId, issue, vendor, notes },
      { headers: { "x-api-key": token, "Content-Type": "application/json" } }
    );
    return response.data;
  }

  // Back from repair: { result, cost, return_notes }
  static async markRepairBack(assetId, { result, cost, return_notes }) {
    const token = localStorage.getItem("token");
    const response = await axios.put(
      `${process.env.REACT_APP_BE_URL}/asset_it/repair-back`,
      { asset_id: assetId, result, cost, return_notes },
      { headers: { "x-api-key": token, "Content-Type": "application/json" } }
    );
    return response.data;
  }

  // Edit history of one asset: who changed what (newest first)
  static async fetchEditHistory(assetId) {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${process.env.REACT_APP_BE_URL}/asset_it/edit-history?id=${encodeURIComponent(assetId)}`,
      { headers: { "x-api-key": token } }
    );
    return response.data.data || [];
  }

  // Retire / Lost / Disposed an asset
  static async retireAsset(assetId, retiredType, retiredReason) {
    const token = localStorage.getItem("token");
    const response = await axios.put(
      `${process.env.REACT_APP_BE_URL}/asset_it/retire-asset`,
      { asset_id: assetId, retired_type: retiredType, retired_reason: retiredReason },
      { headers: { "x-api-key": token, "Content-Type": "application/json" } }
    );
    return response.data;
  }

  // Restore a retired asset (becomes Available again)
  static async restoreAsset(assetId) {
    const token = localStorage.getItem("token");
    const response = await axios.put(
      `${process.env.REACT_APP_BE_URL}/asset_it/restore-asset`,
      { asset_id: assetId },
      { headers: { "x-api-key": token, "Content-Type": "application/json" } }
    );
    return response.data;
  }

  // "I have received this asset" link: current status (no login needed)
  static async getAckStatus(ackToken) {
    const response = await axios.get(
      `${process.env.REACT_APP_BE_URL}/asset_it/ack-status?token=${encodeURIComponent(ackToken)}`
    );
    return response.data.data;
  }

  // "I have received this asset": is the logged-in person the employee? (login needed)
  static async checkAck(ackToken) {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${process.env.REACT_APP_BE_URL}/asset_it/ack-check?token=${encodeURIComponent(ackToken)}`,
      { headers: { "x-api-key": token } }
    );
    return response.data.data;
  }

  // "I have received this asset": confirm (login needed, only that employee)
  static async confirmAck(ackToken) {
    const token = localStorage.getItem("token");
    const response = await axios.put(
      `${process.env.REACT_APP_BE_URL}/asset_it/acknowledge`,
      { token: ackToken },
      { headers: { "x-api-key": token, "Content-Type": "application/json" } }
    );
    return response.data.data;
  }

  // Check if user has valid token
  static getLocalToken() {
    return localStorage.getItem("token");
  }
  static getLocalPermission() {
    const permission = localStorage.getItem("permissionData");
    const permissionArr = permission.split(',')
    return permissionArr
  }
  // Set session storage for login redirect
  static setLoginRedirectUrl(assetId) {
    sessionStorage.setItem("logingsoneurl", `/asset?id=${assetId}`);
  }

  // Open external URL
  static openExternalUrl(url) {
    window.open(url, "_blank");
  }

  // Generate manufacture link
  static generateManufactureLink(assetDetails) {
    if (assetDetails?.manufacture_info?.manufacture_info_link) {
      return assetDetails.manufacture_info.manufacture_info_link;
    } else {
      const brand = assetDetails?.asset_info?.Brand || "";
      const subSeries = assetDetails?.asset_info?.["Sub Series"] || "";
      const series = assetDetails?.asset_info?.["Series"] || "";
      const product = assetDetails?.asset_info?.["Product ID/Model NO"] || "";
      const serial = assetDetails?.asset_info?.["Laptop/Desktop Serial No"] || "";
      const processor = assetDetails?.asset_info?.["Processor"] || "";
      const ram = assetDetails?.asset_info?.["RAM(GB)"] ? assetDetails?.asset_info?.["RAM(GB)"] + ' gb' : "";
      const ssd = assetDetails?.asset_info?.["SSD/HDD"] || "";
      const searchQuery = `${brand} ${series} ${subSeries} ${serial} ${processor} ${ram} ${ssd} ${product}`.trim();
      if (searchQuery) {
        return `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
      }
    }
    return null;
  }
}